import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { BACKLOG_TOP_DEFAULT, type GroomingQueueState } from "@ttc-spectree/toolbox-contract";
import { TOOLBOX_ROLES, TOOLBOX_ROLE_LABELS } from "@ttc-spectree/toolbox-contract/roles";
import { agendaCounts, agendaEntries } from "@/components/grooming/session-build";
import { useGroomingBacklog, useGroomingBatch, useGroomingBatches, useProjectSession } from "@/hooks/queries/use-grooming";
import { canOn, projectAccessOf, projectsWith, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import type { GroomingBacklogItem, GroomingBatch, GroomingProjectSession } from "@/lib/api/grooming";
import type { ToolboxAccessResponse } from "@/lib/api/toolbox-access";
import { dateParts, localToday, readyToPostRun, runMeetingDate } from "@/lib/grooming/agenda-run";
import { readLastProject } from "@/lib/grooming/last-project";
import { formatMeetingDate, prepareByDate } from "@/lib/grooming/meeting-dates";

/**
 * Toolbox home's **Next up** (epic PI-SPEC-SW-E292, feature PI-SPEC-SW-1499;
 * UX-PM-33 and UX-PM-11 on home in docs/analysis/toolbox-ux/03-evaluation.md,
 * Claude Design frame `1f`).
 *
 * Home has no project in its path, so it works on the person's **current
 * project**: the last grooming project shown on this computer when they may
 * still read its backlog there, else the first project they may read. The
 * title-bar search and the command palette use the same project on home.
 *
 * Next up is built only from reads the app already makes, so it adds no API
 * route and no extra ADO read: the project's session (shared agenda and next
 * meeting date), its batch list and the prepared agenda run the Session screen
 * shows (the icon rail's reads), the backlog in its default view (`BACKLOG_TOP_DEFAULT`
 * items in ADO order: the Backlog page's own cache key, so opening the
 * Backlog afterwards reads nothing again) and, in the app, the run queue.
 * Each line is undefined when there is nothing to do, and the lines are
 * hints only: none blocks anything (01-roles.md §9.3). Story Points are never
 * written. The derivations are pure and exported for tests.
 */

// ---------------------------------------------------------------------------
// The current project and the person's role there
// ---------------------------------------------------------------------------

/**
 * The project home works on: `stored` (the last project shown on this
 * computer) when the person may read its backlog, else the first project
 * the access read lists with `read-backlog`, else undefined. Pure.
 */
export function homeProjectOf(
  access: ToolboxAccessResponse | null | undefined,
  stored: string | undefined
): string | undefined {
  if (!access) return undefined;
  if (stored && canOn(access, stored, "read-backlog")) return stored;
  return projectsWith(access, "read-backlog")[0];
}

/**
 * The person's current project for Toolbox home (see {@link homeProjectOf}).
 * The stored project is read again whenever the path changes, so it follows
 * the grooming page the person just left.
 */
export function useNextUpProject(): string | undefined {
  const { pathname } = useLocation();
  const { data: access } = useToolboxAccess();
  // Re-read on every path change: grooming pages write the last project as they are shown.
  const stored = useMemo(() => (pathname ? readLastProject() : undefined), [pathname]);
  return homeProjectOf(access, stored);
}

/**
 * The person's highest role on `project` in plain words ("Project Manager"),
 * from most to least access; "Admin" for a global admin. Null when they hold
 * no role there. Pure.
 */
export function roleWordsOn(access: ToolboxAccessResponse | null | undefined, project: string | undefined): string | null {
  if (!access || !project) return null;
  const roles = projectAccessOf(access, project)?.roles ?? [];
  const highest = TOOLBOX_ROLES.find((role) => roles.includes(role) || (role === "admin" && access.isAdmin));
  return highest ? TOOLBOX_ROLE_LABELS[highest] : null;
}

// ---------------------------------------------------------------------------
// The four lines
// ---------------------------------------------------------------------------

/** The next session: its date, the shared agenda's size, how many have a current brief, and the prepare-by day. */
export interface NextUpSessionLine {
  /** `YYYY-MM-DD`; null when the project has no meeting schedule and the agenda no date. */
  date: string | null;
  agendaCount: number;
  briefedCount: number;
  /** One business day before `date` (`prepareByDate`); null without a date, and for an agenda already prepared. */
  prepareBy: string | null;
}

/** Items below the grooming bar that wait on their authors: asked or not yet. */
export interface NextUpAuthorsLine {
  total: number;
  asked: number;
  notAsked: number;
}

/** What Claude is doing in the app's run queue. */
export interface NextUpClaudeLine {
  /** Briefs running or waiting in the queue. */
  count: number;
  /** The title of the brief running now, when the app reported it. */
  title?: string;
  /** About how many minutes the queue has left, when the project's typical brief time is known. */
  minutesLeft?: number;
}

export interface NextUp {
  session?: NextUpSessionLine;
  /** Briefs written and not posted yet (rows whose next action is Review brief). */
  briefsToPost?: number;
  authors?: NextUpAuthorsLine;
  /** App only: undefined in a browser and while the queue is idle. */
  claude?: NextUpClaudeLine;
  /** True while the session or backlog read is still loading. */
  loading: boolean;
}

/**
 * The session line: undefined when there is neither an agenda nor a next
 * meeting date. With `run`, the prepared agenda run the Session screen shows
 * (`readyToPostRun`: no draft agenda, or only one for a later meeting), the
 * line is that run's meeting, so a draft started for the meeting after it
 * never takes home over (PKG-PM-D Mac walk, 2026-10-23); it has no prepare-by
 * day, as it is prepared. Otherwise the date is the shared agenda's own
 * session date, else the project's next meeting. An agenda item counts as
 * briefed when its brief is current (the Session page's `agendaCounts`). Pure.
 */
export function deriveSessionLine(
  session: GroomingProjectSession | undefined,
  backlogItems: readonly GroomingBacklogItem[] | undefined,
  run?: GroomingBatch | null
): NextUpSessionLine | undefined {
  if (!session) return undefined;
  const byId = new Map((backlogItems ?? []).map((row) => [row.itemId, row] as const));
  if (run) {
    const counts = agendaCounts(agendaEntries(run, byId));
    return { date: runMeetingDate(run, session), agendaCount: counts.items, briefedCount: counts.briefed, prepareBy: null };
  }
  const date = session.batch?.sessionDate ?? session.sessionDate ?? session.nextMeetingDate ?? null;
  const agenda = session.batch;
  if (!agenda && !date) return undefined;
  const counts = agendaCounts(agendaEntries(agenda, byId));
  return {
    date,
    agendaCount: counts.items,
    briefedCount: counts.briefed,
    prepareBy: date ? prepareByDate(date) : null,
  };
}

/** How many briefs wait to be posted: rows whose next action is Review brief; undefined when none. Pure. */
export function deriveBriefsToPost(rows: readonly Pick<GroomingBacklogItem, "nextAction">[] | undefined): number | undefined {
  const count = (rows ?? []).filter((row) => row.nextAction === "review-brief").length;
  return count > 0 ? count : undefined;
}

/** Items below the grooming bar (Needs information) and whether their authors were asked; undefined when none. Pure. */
export function deriveAuthorsLine(
  rows: readonly Pick<GroomingBacklogItem, "stage" | "authorAsk">[] | undefined
): NextUpAuthorsLine | undefined {
  const below = (rows ?? []).filter((row) => row.stage === "needs-information");
  if (below.length === 0) return undefined;
  const asked = below.filter((row) => row.authorAsk).length;
  return { total: below.length, asked, notAsked: below.length - asked };
}

/**
 * What Claude is preparing: the brief runs (`groom-item` entries) running or
 * waiting in the app's queue, the running one's title, and about how many
 * minutes are left from the project's typical brief time and how many run at
 * once. Undefined when the queue is idle or the app has none. Pure.
 */
export function deriveClaudeLine(
  state: GroomingQueueState | null | undefined,
  typicalItemSeconds: number | null | undefined
): NextUpClaudeLine | undefined {
  if (!state) return undefined;
  const running = state.running.filter((entry) => entry.kind === "groom-item");
  const waiting = state.upNext.filter((entry) => entry.kind === "groom-item");
  const count = running.length + waiting.length;
  if (count === 0) return undefined;
  const line: NextUpClaudeLine = { count };
  const title = running.find((entry) => entry.title)?.title;
  if (title) line.title = title;
  if (typicalItemSeconds && typicalItemSeconds > 0) {
    const atOnce = Math.max(1, state.concurrency);
    line.minutesLeft = Math.max(1, Math.ceil((count * typicalItemSeconds) / atOnce / 60));
  }
  return line;
}

/** The Next up lines for `project` (undefined: no project, no lines). */
export function useNextUp(project: string | undefined): NextUp {
  const session = useProjectSession(project);
  const backlog = useGroomingBacklog(project, BACKLOG_TOP_DEFAULT, "rank");
  const batches = useGroomingBatches(project);
  const runEntry = readyToPostRun(batches.data, session.data, localToday());
  const run = useGroomingBatch(runEntry?.id);
  const { supported, state } = useGroomingQueue();
  return useMemo((): NextUp => {
    if (!project) return { loading: false };
    const rows = backlog.data?.items;
    const runLoading = batches.isLoading || (runEntry !== undefined && run.isLoading);
    const result: NextUp = { loading: session.isLoading || backlog.isLoading || runLoading };
    // A run whose read failed gives no line rather than the later draft's meeting; so does a batch list that is not
    // known (its first read failed), as it may hold the coming meeting's run (Copilot review of #545). Data cached
    // from an earlier read still counts after a background error.
    const runRead = runEntry ? run.data : null;
    const batchesKnown = batches.data !== undefined;
    const sessionLine =
      runLoading || !batchesKnown || runRead === undefined ? undefined : deriveSessionLine(session.data, rows, runRead);
    if (sessionLine) result.session = sessionLine;
    const briefs = deriveBriefsToPost(rows);
    if (briefs !== undefined) result.briefsToPost = briefs;
    const authors = deriveAuthorsLine(rows);
    if (authors) result.authors = authors;
    const claude = supported ? deriveClaudeLine(state, session.data?.typicalItemSeconds) : undefined;
    if (claude) result.claude = claude;
    return result;
  }, [
    project,
    session.data,
    session.isLoading,
    backlog.data,
    backlog.isLoading,
    batches.isLoading,
    batches.data,
    runEntry,
    run.data,
    run.isLoading,
    supported,
    state,
  ]);
}

// ---------------------------------------------------------------------------
// Words (frame 1f)
// ---------------------------------------------------------------------------

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/** Whole days from `today` to `date` (both `YYYY-MM-DD`). */
function daysFrom(today: string, date: string): number {
  return Math.round((dateParts(date).getTime() - dateParts(today).getTime()) / 86_400_000);
}

/** "Next session: Tuesday, Oct 27", or "Next session: no date yet". */
export function sessionTitle(line: NextUpSessionLine): string {
  return line.date ? `Next session: ${formatMeetingDate(line.date)}` : "Next session: no date yet";
}

/**
 * "2 on the agenda · both briefed · prepare by Monday" (frame 1f). The
 * prepare-by part names the weekday within the coming week, says "prepare
 * today" on the day, and is left out once that day has passed. A hint only.
 */
export function sessionDetail(line: NextUpSessionLine, today: string = localToday()): string {
  const parts: string[] = [];
  if (line.agendaCount === 0) {
    parts.push("nothing on the agenda yet");
  } else {
    parts.push(`${String(line.agendaCount)} on the agenda`);
    if (line.briefedCount === line.agendaCount) {
      parts.push(line.agendaCount === 1 ? "briefed" : line.agendaCount === 2 ? "both briefed" : "all briefed");
    } else if (line.briefedCount === 0) {
      parts.push("none briefed yet");
    } else {
      parts.push(`${String(line.briefedCount)} briefed`);
    }
  }
  if (line.prepareBy) {
    const days = daysFrom(today, line.prepareBy);
    if (days === 0) parts.push("prepare today");
    else if (days > 0 && days < 7) {
      parts.push(`prepare by ${dateParts(line.prepareBy).toLocaleDateString("en-US", { weekday: "long" })}`);
    } else if (days >= 7) parts.push(`prepare by ${formatMeetingDate(line.prepareBy)}`);
  }
  const text = parts.join(" · ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "3 briefs ready to post". */
export function briefsTitle(count: number): string {
  return `${plural(count, "brief", "briefs")} ready to post`;
}

/** "2 items wait on their authors". */
export function authorsTitle(line: NextUpAuthorsLine): string {
  return line.total === 1 ? "1 item waits on its author" : `${String(line.total)} items wait on their authors`;
}

/** "1 asked · 1 not asked yet". */
export function authorsDetail(line: NextUpAuthorsLine): string {
  return `${String(line.asked)} asked · ${String(line.notAsked)} not asked yet`;
}

/** "Claude is preparing 1 brief". */
export function claudeTitle(line: NextUpClaudeLine): string {
  return `Claude is preparing ${plural(line.count, "brief", "briefs")}`;
}

/** "<item title> · about 4 minutes left", either part when known; "Waiting in the queue" with neither. */
export function claudeDetail(line: NextUpClaudeLine): string {
  const minutes = line.minutesLeft ? `about ${plural(line.minutesLeft, "minute", "minutes")} left` : null;
  if (line.title && minutes) return `${line.title} · ${minutes}`;
  if (line.title) return line.title;
  if (minutes) return minutes.charAt(0).toUpperCase() + minutes.slice(1);
  return "Waiting in the queue";
}
